import { plateFont, numFont } from '../../utils/fonts';
import * as React from 'react';
import { Box, Typography, IconButton, Chip, Button, CircularProgress, Tooltip } from '@mui/material';
import ViewSidebarIcon from '@mui/icons-material/ViewSidebar';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { withTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useUserStore } from '../../store';
import { can } from '../../utils/access';
import useCalendarState from './hooks/useCalendarState';
import useCalendarColors from './hooks/useCalendarColors';
import { deriveTagTextColor } from './calendarColors';
import { SIDEBAR_WIDTH } from './calendarConstants';

function CalendarSidebar({ t }) {
  const canSchedule = useUserStore((s) => can(s.currentOrganization, 'work_orders.schedule'));
  const navigate = useNavigate();
  const {
    sidebarCollapsed,
    toggleSidebar,
    unscheduledMaintenances,
    unscheduledTotalCount,
    unscheduledHasMore,
    unscheduledLoadingMore,
    openScheduleDialog,
    setDraggedMaintenance,
    highlightedUnscheduledId,
    appendUnscheduledMaintenances,
    setUnscheduledLoadingMore,
    calendarMode,
    enterSchedulingMode,
  } = useCalendarState();
  // The month grid has no drop target: a click arms scheduling mode and the day cell finishes it.
  // Otherwise the panel is the same in every mode, collapsed by the same toggle.
  const isMonthMode = calendarMode === 'month';
  const collapsed = sidebarCollapsed;
  const { tagsById } = useCalendarColors();

  // Reference for scrolling to highlighted item
  const highlightedRef = React.useRef(null);

  // Scroll to highlighted item when it changes
  React.useEffect(() => {
    if (highlightedUnscheduledId && highlightedRef.current) {
      highlightedRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [highlightedUnscheduledId]);

  const handleDragStart = (e, maintenance) => {
    e.dataTransfer.setData('application/json', JSON.stringify({
      type: 'unscheduled-maintenance',
      maintenance: maintenance,
    }));
    e.dataTransfer.effectAllowed = 'move';
    setDraggedMaintenance(maintenance);
  };

  const handleDragEnd = () => {
    setDraggedMaintenance(null);
  };

  const handleLoadMore = async () => {
    setUnscheduledLoadingMore(true);
    try {
      const response = await axios.get('/api/scheduling/calendar/unscheduled/', {
        params: { limit: 50, offset: unscheduledMaintenances.length },
      });
      appendUnscheduledMaintenances(response.data.results, response.data.has_more);
    } catch (err) {
      console.error('Error loading more unscheduled:', err);
    } finally {
      setUnscheduledLoadingMore(false);
    }
  };

  return (
    <Box
      data-testid="calendar-unscheduled-sidebar"
      sx={{
        width: collapsed ? 48 : SIDEBAR_WIDTH,
        bgcolor: 'background.paper',
        borderRight: 1,
        borderColor: 'divider',
        display: 'flex',
        flexDirection: 'column',
        transition: 'width 0.2s ease',
        overflow: 'hidden',
        flexShrink: 0,
      }}
    >
      {/* Header */}
      <Box
        sx={{
          p: 2,
          borderBottom: 1,
          borderColor: 'divider',
          display: 'flex',
          alignItems: 'center',
          justifyContent: collapsed ? 'center' : 'space-between',
        }}
      >
        {!collapsed && (
          <Typography
            sx={{
              fontSize: '14px',
              fontWeight: 600,
              whiteSpace: 'nowrap',
              fontFamily: "'Plus Jakarta Sans', sans-serif",
            }}
          >
            {t('Unscheduled')} ({unscheduledTotalCount})
          </Typography>
        )}
        <IconButton
          onClick={toggleSidebar}
          size="small"
          sx={{
            color: 'text.secondary',
            '&:hover': { color: 'text.primary' },
          }}
        >
          <ViewSidebarIcon fontSize="small" />
        </IconButton>
      </Box>

      {/* Content */}
      {!collapsed && (
        <Box sx={{ flex: 1, overflow: 'auto', p: 1.5 }}>
          <Typography
            sx={{
              fontSize: '12px',
              color: 'text.secondary',
              mb: 1.5,
              lineHeight: 1.4,
            }}
          >
            {isMonthMode ? t('calendar.month.sidebarHint') : t('Drag maintenance to calendar')}
          </Typography>

          {unscheduledMaintenances.length === 0 ? (
            <Box sx={{ py: 4, textAlign: 'center' }}>
              <Typography sx={{ fontSize: '13px', color: 'text.disabled' }}>
                {t('No unscheduled maintenances')}
              </Typography>
            </Box>
          ) : (
            <>
            {unscheduledMaintenances.map((maintenance) => {
              const isHighlighted = highlightedUnscheduledId === maintenance.id;
              // Same colour as the work order's cards on the grid, so the sidebar and the
              // calendar read as one board.
              const tag = maintenance.color_tag_id ? tagsById[maintenance.color_tag_id] : null;
              const tagIsFill = tag && tag.display_mode !== 'border';
              return (
                <Box
                  key={maintenance.id}
                  data-testid={`unscheduled-maintenance-${maintenance.id}`}
                  data-color-tag={maintenance.color_tag_id != null ? String(maintenance.color_tag_id) : undefined}
                  ref={isHighlighted ? highlightedRef : null}
                  draggable={canSchedule && !isMonthMode}
                  onDragStart={(e) => canSchedule && !isMonthMode && handleDragStart(e, maintenance)}
                  onDragEnd={handleDragEnd}
                  onClick={() => {
                    if (!canSchedule) return;
                    if (isMonthMode) {
                      enterSchedulingMode(maintenance);
                    } else {
                      openScheduleDialog(maintenance);
                    }
                  }}
                  sx={{
                    p: 1.5,
                    bgcolor: isHighlighted
                      ? 'rgba(237, 137, 54, 0.15)'
                      : tagIsFill
                      ? tag.color
                      : 'background.default',
                    color: !isHighlighted && tagIsFill ? deriveTagTextColor(tag.color) : undefined,
                    borderRadius: '8px',
                    mb: 1,
                    cursor: isMonthMode ? 'pointer' : 'grab',
                    border: isHighlighted || (tag && !tagIsFill) ? 2 : 1,
                    borderColor: isHighlighted ? '#ed8936' : tag && !tagIsFill ? tag.color : 'divider',
                    transition: 'all 0.15s ease',
                    animation: isHighlighted ? 'pulseHighlight 1s ease-in-out 3' : undefined,
                    '@keyframes pulseHighlight': {
                      '0%, 100%': { transform: 'scale(1)', boxShadow: '0 0 0 0 rgba(237, 137, 54, 0.4)' },
                      '50%': { transform: 'scale(1.02)', boxShadow: '0 0 0 4px rgba(237, 137, 54, 0.2)' },
                    },
                    '&:hover': {
                      borderColor: isHighlighted ? '#ed8936' : 'secondary.main',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                    },
                    '&:active': {
                      cursor: isMonthMode ? 'pointer' : 'grabbing',
                    },
                  }}
                >
                <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                      sx={{
                        ...plateFont,
                        fontSize: '14px',
                        fontWeight: 600,
                      }}
                    >
                      {maintenance.vehicle_registration}
                    </Typography>
                    {(maintenance.vehicle_manufacturer || maintenance.vehicle_model) && (
                      <Typography
                        sx={{
                          fontSize: '11px',
                          color: 'text.secondary',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {[maintenance.vehicle_manufacturer, maintenance.vehicle_model].filter(Boolean).join(' ')}
                      </Typography>
                    )}
                    {maintenance.work_number && (
                      <Typography
                        sx={{
                          fontSize: '11px',
                          color: 'text.secondary',
                          ...numFont,
                        }}
                      >
                        #{maintenance.work_number}
                      </Typography>
                    )}
                  </Box>
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.25, ml: 0.5 }}>
                    <Tooltip title={t('View details')} placement="left">
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/maintenances/${maintenance.id}`);
                        }}
                        sx={{
                          p: 0.5,
                          color: 'text.disabled',
                          '&:hover': { color: 'secondary.main', bgcolor: 'action.hover' },
                        }}
                      >
                        <OpenInNewIcon sx={{ fontSize: 18 }} />
                      </IconButton>
                    </Tooltip>
                    {!isMonthMode && <DragIndicatorIcon sx={{ fontSize: 20, color: 'text.disabled' }} />}
                  </Box>
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5, mb: 0.75 }}>
                  <Typography sx={{ fontSize: '12px', color: 'text.secondary' }}>
                    {maintenance.customer_name}
                  </Typography>
                  {maintenance.priority === 'high' && (
                    <Chip
                      label={t('Urgent')}
                      size="small"
                      sx={{
                        height: 18,
                        fontSize: '10px',
                        fontWeight: 600,
                        textTransform: 'uppercase',
                        bgcolor: '#fee2e2',
                        color: '#dc2626',
                        '& .MuiChip-label': { px: 0.75 },
                      }}
                    />
                  )}
                </Box>

                <Typography sx={{ fontSize: '13px', color: 'text.primary' }}>
                  {maintenance.description}
                </Typography>
              </Box>
              );
            })}
            {unscheduledHasMore && (
              <Button
                onClick={handleLoadMore}
                disabled={unscheduledLoadingMore}
                fullWidth
                size="small"
                sx={{
                  mt: 1,
                  mb: 1,
                  fontSize: '12px',
                  textTransform: 'none',
                  color: 'text.secondary',
                }}
              >
                {unscheduledLoadingMore ? (
                  <CircularProgress size={16} sx={{ mr: 1 }} />
                ) : null}
                {t('Load more')}
              </Button>
            )}
            </>
          )}
        </Box>
      )}
    </Box>
  );
}

export default withTranslation()(CalendarSidebar);
